import React, { useState, useEffect, useCallback, useRef } from "react";
import { Route, Switch, Link, useHistory, useLocation } from "react-router-dom";
import { Layout, Row, Col, Menu, Breadcrumb, Affix, Popover, Button } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BookOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import queryString from "query-string";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import axios from "axios";

import AppSwitcherIcon from "./assets/app-switcher-icon.png";
import { initUserApiInterceptors, getAppTenantName } from "./apis/user";
import { initWorkOrderdApiInterceptors } from "./apis/workOrder";
import { initERPApiInterceptors } from "./apis/erpSyncBlueprint";
import { useRoleContext } from "./contexts/Role";
import { getToken, getUser } from "./utils/cookies";
import ERPSyncManual from "./pages/ERPSyncManual";
import SpecSetting from "./pages/SpecSetting";
import SpecHistory from "./pages/SpecHistory";

import "./App.css";

const { Header, Sider, Content } = Layout;

const App = () => {
  const history = useHistory();
  const location = useLocation();

  const { appRole } = useRoleContext();

  const user = getUser();
  const tenantId = user ? JSON.parse(user).tenantId : "";

  const [isLogin, setIsLogin] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [appSwitcherVisible, setAppSwitcherVisible] = useState(false);
  const [appSwitcherClassname, setAppSwitcherClassname] = useState("");

  const appSwitcherRef = useRef(false);

  const menuItems = [
    {
      key: "/",
      icon: <BookOutlined />,
      label: "ERP 同步手冊",
    },
    {
      key: "/history",
      icon: <FileDoneOutlined />,
      label: "配置歷史",
    },
    appRole !== "onlyView"
      ? {
        key: "/setting",
        icon: <FileTextOutlined />,
        label: "配置設定",
      }
      : {},
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: (
        <a
          href={
            process.env.REACT_APP_SSO_URL +
            "/logout?tenantId=" +
            tenantId +
            "&redirectUri=" +
            window.location.origin
          }
        >
          登出
        </a>
      ),
    },
  ];

  function getMenuSelectedKey() {
    const pathArr = location.pathname.split("/");
    const path = "/" + pathArr[1];
    return path;
  }

  const getAuth = useCallback(() => {
    //// check the permission
    if (getUser() !== undefined && getToken() !== undefined) {
      let tenantId = JSON.parse(getUser()).tenantId;
      let reloadUri = `/login?tenantId=${tenantId}&redirectUri=`;
      Cookies.set("reloadUri", reloadUri, {
        domain: process.env.REACT_APP_DOMAIN,
        expires: 365,
      });

      //// user-api/permission/app
      axios
        .get(process.env.REACT_APP_USER_URL + "/permission/app", {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          params: {
            name: "erp",
          },
        })
        .then((res) => {
          if (res["data"]["access"]) {
            getAppTenantName()
              .then((response) =>
                sessionStorage.setItem("tenantName", response["data"]["title"])
              )
              .catch((error) =>
                console.error("getAppTenantName error: ", error)
              );

            initUserApiInterceptors();
            initWorkOrderdApiInterceptors();
            initERPApiInterceptors();
            setIsLogin(true);

            let expireTime = jwtDecode(getToken()).exp;
            let currentTime = dayjs().unix();
            let refreshTimeout = expireTime - currentTime - 300; // refresh token 5 minutes before token expire

            setTimeout(() => {
              getAuth();
            }, refreshTimeout * 1000);
          } else {
            //// access is false
            alert("You don't have permission to access ERP-sync-blueprint");
            window.location.replace(
              process.env.REACT_APP_SSO_URL + reloadUri + window.location.origin
            );
          }
        })
        .catch((err) => {
          console.error("getPermission error: ", err);
          window.location.replace(
            process.env.REACT_APP_SSO_URL + reloadUri + window.location.origin
          );
        });
    } else {
      //// no user or token
      const params = queryString.parse(window.location.search);
      let cookieReloadUri = Cookies.get("reloadUri");

      if (params?.tenantId) {
        //// tenantId is given in Url
        window.location.replace(
          process.env.REACT_APP_SSO_URL +
          `/login?tenantId=${params?.tenantId}&redirectUri=` +
          window.location.href
        );
      } else if (cookieReloadUri) {
        //// this user has logged in before
        window.location.replace(
          process.env.REACT_APP_SSO_URL + cookieReloadUri + window.location.href
        );
      } else {
        window.location.replace(
          process.env.REACT_APP_SSO_URL +
          `/login?redirectUri=` +
          window.location.href
        );
      }
    }
  }, []);

  useEffect(() => {
    getAuth();
  }, [getAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return isLogin && appRole ? (
    <Layout>
      <Sider
        className="app-sider"
        collapsible
        collapsed={collapsed}
        collapsedWidth={80}
        trigger={null}
      >
        <div
          className="sider-home-title"
          onClick={() => { history.push("/"); }}
        >
          <Row gutter={[0, 0]} style={{ marginTop: '10px' }}>
            <Col span={collapsed ? window.innerWidth > 1200 ? 24 : 0 : 8}>
              <div style={{ display: 'grid' }}>
                <img src={"/ERP_BLUEPRINT_DARK.png"} alt="logo" style={{ margin: 'auto' }} />
              </div>
            </Col>
            <Col span={collapsed ? 0 : 16} style={{ margin: 'auto' }}>
              <div> ERP 同步藍圖 </div>
              <div style={{ fontSize: '10px' }}> ERP Sync Blueprint </div>
            </Col>
          </Row>
        </div>
        <Menu
          className="sider-menu"
          mode="inline"
          theme="dark"
          selectedKeys={getMenuSelectedKey()}
          items={menuItems}
          onClick={(item) =>
            item["key"] !== "logout" && history.push(item["key"])
          }
        />
      </Sider>

      <Layout>
        <Header className="app-header">
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              className: "trigger",
              onClick: () => setCollapsed(!collapsed),
            }
          )}
          <div className="header-items">
            <Popover
              classNames={{ root: `app-switcher${appSwitcherClassname}` }}
              title={null}
              placement="bottom"
              trigger="click"
              open={appSwitcherClassname === "" ? true : appSwitcherVisible}
              onOpenChange={() => {
                if (appSwitcherRef.current) {
                  setAppSwitcherClassname("-popover");
                  setAppSwitcherVisible((v) => !v);
                }
              }}
              content={
                <div className="mask">
                  <iframe
                    className="app-switcher-iframe"
                    src={process.env.REACT_APP_SWITCHER_URL}
                    title="app-switcher"
                    frameBorder="0"
                  />
                </div>
              }
              styles={{ body: { borderRadius: "15px" } }}
            >
              <Button
                icon={
                  <img
                    src={AppSwitcherIcon}
                    alt="app-switcher-icon"
                    height={15}
                  />
                }
                type="text"
                onClick={() => {
                  if (!appSwitcherRef.current) {
                    setAppSwitcherClassname("-popover");
                    setAppSwitcherVisible(true);
                    appSwitcherRef.current = true;
                  }
                }}
              />
            </Popover>
          </div>
        </Header>

        <Content className="app-content">
          <Affix offsetTop={50}>
            <Breadcrumb
              className="app-content-breadcrumb"
              items={[
                { title: <Link to="/">首頁</Link> },
                {
                  title: menuItems.find(
                    (item) => item["key"] === getMenuSelectedKey()
                  )?.["label"],
                },
              ]}
            />
          </Affix>

          <Switch>
            <Route exact path="/" component={ERPSyncManual} />
            <Route path="/setting/:uuid?" component={SpecSetting} />
            <Route path="/history" component={SpecHistory} />
            <Route exact path="/*" component={ERPSyncManual} />
          </Switch>
        </Content>
      </Layout>
    </Layout>
  ) : null;
};

export default App;
